import BaseListStore, { BaseListStoreArgs } from 'stores/BaseListStore';
import { action, makeObservable, runInAction } from 'mobx';
import { IDataFetchOptions } from '../types';
import getAnalysisContext from '../helpers/getAnalysisContext';
import { getConditions } from 'stores/_services/filterService';
import { get, getPage } from 'stores/_services/dataService';

export interface IFileInstance {
    id: string;
    sha1: string;
    fileRepId: string;
    name?: string;
}

interface IInterfaceListStoreArgs<T> extends BaseListStoreArgs<T> {
    baseRoute: string;
    itemKey?: string;
    itemId?: string;
    searchFields?: string[];
}

export default class BaseInstanceListStore<T> extends BaseListStore<T> {
    protected readonly itemKey?: string;
    protected readonly itemId?: string;
    protected baseRoute: string;
    protected searchFields?: string[];

    constructor({ baseRoute, itemKey, itemId, searchFields, ...listStoreArgs }: IInterfaceListStoreArgs<T>) {
        super(listStoreArgs);

        makeObservable(this, {
            getInstances: action
        });

        this.baseRoute = baseRoute;
        this.searchFields = searchFields;

        if (itemKey && itemId) {
            this.itemKey = itemKey;
            this.itemId = itemId;
        }
    }

    // public get instances(): unknown {
    //     throw new Error('Instances getter undefined')
    // }

    async getInstances(args: IDataFetchOptions = {}): Promise<void> {
        const shouldReset = this._updateListState(args);
        const { analysisFilter } = await getAnalysisContext();

        const conditions = getConditions(this.filter) as Record<string, unknown>[];

        if (analysisFilter) {
            conditions.push(analysisFilter);
        }

        if (this.itemId && this.itemKey) {
            conditions.push({ [this.itemKey]: this.itemId });
        }

        const options = {
            count: false,
            filter: {
                limit: this.limit,
                skip: this.skip,
                searchFields: this.searchFields,
                where: conditions,
                order: this.sort
            }
        };

        const res = await getPage(this.baseRoute, options);

        const { data } = res;

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, this.skip);

        if (data.length === this.limit) {
            runInAction(() => {
                this.count += this.count == 0 ? this.limit * 2 : this.limit;
            });
        } else {
            runInAction(() => {
                this.count = this.count < this.limit ? data.length : Math.max(0, this.count - this.limit - data.length);
            });
        }
    }

    async getInstance(instanceId: string): Promise<void> {
        const { data } = await get(this.baseRoute, { filter: { where: { id: instanceId } } });

        this._updateData(data);
    }
}
